import { rpx } from 'client/lib/rpx-client';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { LoadingIndicator } from '@components/loading-indicator';
import { showError } from '@components/app-error';
import type { AccountKind } from 'shared/account-classification';
import type { AipPaymentStatus, AipStatus, RosterRow } from 'server/rpx/endpoints/course-roster';

type Course = { id: UUID; title: string };

const AIP_STATUS_LABEL: Record<AipStatus, string> = {
  active: 'Active',
  cancelled: 'Cancelled',
  never: 'Never',
};

const AIP_STATUS_CLASS: Record<AipStatus, string> = {
  active: 'text-green-700 bg-green-50',
  cancelled: 'text-orange-700 bg-orange-50',
  never: 'text-gray-500 bg-gray-100',
};

const AIP_PAYMENT_LABEL: Record<AipPaymentStatus, string> = {
  subscription_active: 'Active sub',
  card_failing: 'Card failing',
  unknown: 'Unknown',
  cancelling: 'Cancelling',
  payment_plan: 'Payment plan',
  paid: 'Paid',
  free_access: 'Free access',
  bundle: 'Bundle',
  manual: 'Manual',
  none: '—',
};

// Filter dropdown labels. Same statuses as AIP_PAYMENT_LABEL but reworded
// where the cell label only reads well in-context (e.g. "—" for `none`).
const AIP_PAYMENT_FILTER_LABEL: Record<AipPaymentStatus, string> = {
  subscription_active: 'Active sub',
  manual: 'Manual',
  payment_plan: 'Payment plan',
  paid: 'Paid',
  free_access: 'Free access (price)',
  bundle: 'Bundle',
  cancelling: 'Cancelling',
  card_failing: 'Card failing',
  unknown: 'Unknown',
  none: 'No AIP payment',
};

const AIP_PAYMENT_FILTER_OPTIONS: AipPaymentStatus[] = [
  'subscription_active',
  'manual',
  'payment_plan',
  'paid',
  'free_access',
  'bundle',
  'cancelling',
  'card_failing',
  'unknown',
  'none',
];

const AIP_PAYMENT_CLASS: Record<AipPaymentStatus, string> = {
  subscription_active: 'text-green-700',
  card_failing: 'text-red-700 font-medium',
  unknown: 'text-amber-700 font-medium',
  cancelling: 'text-orange-700',
  payment_plan: 'text-blue-700',
  paid: 'text-green-700',
  free_access: 'text-gray-700',
  bundle: 'text-purple-700',
  manual: 'text-gray-500',
  none: 'text-gray-400',
};

const KIND_PILL_LABEL: Record<AccountKind, string> = {
  staff: 'STAFF',
  test: 'TEST',
  member: '',
};

const KIND_PILL_CLASS: Record<AccountKind, string> = {
  staff: 'bg-gray-200 text-gray-700',
  test: 'bg-amber-100 text-amber-800',
  member: '',
};

// Reduce verbose price names to a short scannable form. Falls back to the
// raw name so we don't lose information when a new price shape lands.
// Full name still shown on hover via `title=` on the cell.
function compactPrice(name?: string, unitAmount?: number): string {
  if (!name) {
    return '';
  }
  const trimmed = name.trim();
  if (/^\$97\/Month Subscription/i.test(trimmed)) {
    return '$97/mo AIP';
  }
  if (/^AIP Members - Free Access/i.test(trimmed)) {
    return 'AIP Free Access';
  }
  if (/^AISFull$/i.test(trimmed)) {
    const dollars = unitAmount && unitAmount > 0 ? ` ($${Math.round(unitAmount / 100)})` : '';
    return `AIS full pay${dollars}`;
  }
  if (/^AISPayments$/i.test(trimmed)) {
    return 'AIS payment plan';
  }
  if (unitAmount && unitAmount > 0) {
    return `${trimmed} ($${(unitAmount / 100).toFixed(2)})`;
  }
  return trimmed;
}

function fullPrice(name?: string, unitAmount?: number): string {
  if (!name) {
    return '';
  }
  if (unitAmount && unitAmount > 0) {
    return `${name} ($${(unitAmount / 100).toFixed(2)})`;
  }
  return name;
}

function formatRelativeDate(iso?: string) {
  if (!iso) {
    return '—';
  }
  const then = new Date(iso).getTime();
  const days = Math.floor((Date.now() - then) / (24 * 60 * 60 * 1000));
  if (days === 0) {
    return 'today';
  }
  if (days === 1) {
    return 'yesterday';
  }
  if (days < 30) {
    return `${days}d ago`;
  }
  if (days < 365) {
    return `${Math.floor(days / 30)}mo ago`;
  }
  return `${Math.floor(days / 365)}y ago`;
}

function formatShortDate(iso?: string) {
  if (!iso) {
    return '';
  }
  return iso.slice(0, 10);
}

function readInitialCourseId(courses: Course[]): UUID | undefined {
  if (typeof window === 'undefined') {
    return;
  }
  const id = new URLSearchParams(window.location.search).get('courseId') as UUID | null;
  if (id && courses.some((c) => c.id === id)) {
    return id;
  }
  return;
}

function writeCourseIdToUrl(courseId: UUID | undefined) {
  if (typeof window === 'undefined') {
    return;
  }
  const params = new URLSearchParams(window.location.search);
  params.set('tab', 'roster');
  if (courseId) {
    params.set('courseId', courseId);
  } else {
    params.delete('courseId');
  }
  const newUrl = `${window.location.pathname}?${params.toString()}`;
  window.history.replaceState({}, '', newUrl);
}

export function RosterTab(props: { courses: Course[] }) {
  const [selectedCourseId, setSelectedCourseId] = useState<UUID | undefined>(() =>
    readInitialCourseId(props.courses),
  );
  const [isLoading, setIsLoading] = useState(false);
  const [rows, setRows] = useState<RosterRow[]>([]);
  const [aipActiveOnly, setAipActiveOnly] = useState(false);
  const [hideStaffTest, setHideStaffTest] = useState(true);
  const [aipPaymentFilter, setAipPaymentFilter] = useState<AipPaymentStatus | 'all'>('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    writeCourseIdToUrl(selectedCourseId);
    if (!selectedCourseId) {
      setRows([]);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    rpx.courseRoster
      .getCourseRoster({ courseId: selectedCourseId })
      .then((result) => {
        if (cancelled) {
          return;
        }
        setRows(result.rows);
      })
      .catch(showError)
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [selectedCourseId]);

  const hiddenCount = useMemo(
    () => (hideStaffTest ? rows.filter((r) => r.kind !== 'member').length : 0),
    [rows, hideStaffTest],
  );

  const filteredRows = useMemo(() => {
    const lower = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (hideStaffTest && row.kind !== 'member') {
        return false;
      }
      if (aipActiveOnly && row.aipStatus !== 'active') {
        return false;
      }
      if (aipPaymentFilter !== 'all' && row.aipPaymentStatus !== aipPaymentFilter) {
        return false;
      }
      if (
        lower &&
        !row.name.toLowerCase().includes(lower) &&
        !row.email.toLowerCase().includes(lower)
      ) {
        return false;
      }
      return true;
    });
  }, [rows, aipActiveOnly, hideStaffTest, aipPaymentFilter, search]);

  const csvUrl = useMemo(() => {
    if (!selectedCourseId) {
      return;
    }
    const params = new URLSearchParams({ courseId: selectedCourseId });
    if (aipActiveOnly) {
      params.set('aipActiveOnly', 'true');
    }
    if (hideStaffTest) {
      params.set('hideStaffTest', 'true');
    }
    if (aipPaymentFilter !== 'all') {
      params.set('aipPaymentStatus', aipPaymentFilter);
    }
    return `/csv/course-roster.csv?${params.toString()}`;
  }, [selectedCourseId, aipActiveOnly, hideStaffTest, aipPaymentFilter]);

  return (
    <div>
      <p class="-mt-2 mb-6 text-sm text-gray-500">
        Roster of every member in a course with their AI Playground status, payment status, source,
        and engagement. Pick a course to see who's enrolled and why.
      </p>

      <div class="flex flex-wrap items-center gap-3 mb-6">
        <label class="text-sm text-gray-700 font-medium">Course</label>
        <select
          class="inline-ruz-input"
          value={selectedCourseId ?? ''}
          onChange={(e) => {
            const value = (e.target as HTMLSelectElement).value;
            setSelectedCourseId(value ? (value as UUID) : undefined);
          }}
        >
          <option value="">Select a course…</option>
          {props.courses.map((c) => (
            <option key={c.id} value={c.id}>
              {c.title}
            </option>
          ))}
        </select>

        {selectedCourseId && (
          <>
            <input
              type="search"
              class="inline-ruz-input"
              placeholder="Search name or email..."
              value={search}
              onInput={(e) => setSearch((e.target as HTMLInputElement).value)}
              style="min-width: 240px"
            />

            <label class="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={aipActiveOnly}
                onChange={(e) => setAipActiveOnly((e.target as HTMLInputElement).checked)}
              />
              AIP active only
            </label>

            <label class="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={hideStaffTest}
                onChange={(e) => setHideStaffTest((e.target as HTMLInputElement).checked)}
              />
              Hide staff & test
              {hideStaffTest && hiddenCount > 0 && (
                <span class="text-xs text-gray-500">({hiddenCount} hidden)</span>
              )}
            </label>

            <label class="flex items-center gap-2 text-sm text-gray-700">
              <span>AIP payment</span>
              <select
                class="inline-ruz-input"
                value={aipPaymentFilter}
                onChange={(e) =>
                  setAipPaymentFilter(
                    (e.target as HTMLSelectElement).value as AipPaymentStatus | 'all',
                  )
                }
              >
                <option value="all">All</option>
                {AIP_PAYMENT_FILTER_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {AIP_PAYMENT_FILTER_LABEL[s]}
                  </option>
                ))}
              </select>
            </label>

            {csvUrl && (
              <a
                href={csvUrl}
                class="ml-auto inline-block text-sm text-indigo-700 hover:text-indigo-900"
                download
              >
                Download CSV
              </a>
            )}
          </>
        )}
      </div>

      {!selectedCourseId && (
        <div class="py-16 text-center border border-dashed border-gray-200 rounded-md bg-gray-50/50">
          <p class="text-sm font-medium text-gray-700">Pick a course to view its roster</p>
          <p class="mt-1 text-xs text-gray-500">
            Use the Course picker above. The roster loads on demand and can take a few seconds for
            larger cohorts.
          </p>
        </div>
      )}

      {selectedCourseId && isLoading && (
        <div class="py-8">
          <LoadingIndicator />
        </div>
      )}

      {selectedCourseId && !isLoading && (
        <>
          <div class="mb-3 text-sm text-gray-500">
            <span class="font-medium text-gray-900">{filteredRows.length}</span>{' '}
            {filteredRows.length === 1 ? 'member' : 'members'}
            {filteredRows.length < rows.length && (
              <span class="text-gray-400">
                {' '}
                ({rows.length - filteredRows.length} hidden by filters)
              </span>
            )}
            {aipActiveOnly && ' • AIP active only'}
            {aipPaymentFilter !== 'all' &&
              ` • AIP payment: ${AIP_PAYMENT_FILTER_LABEL[aipPaymentFilter]}`}
          </div>

          <div class="overflow-x-auto border border-gray-200 rounded-md">
            <table class="text-sm w-full" style="min-width: 1200px; table-layout: fixed">
              <colgroup>
                <col style="width: 22%; min-width: 240px" />
                <col style="width: 8%; min-width: 96px" />
                <col style="width: 10%; min-width: 110px" />
                <col style="width: 11%; min-width: 130px" />
                <col style="width: 18%; min-width: 200px" />
                <col style="width: 8%; min-width: 92px" />
                <col style="width: 5%; min-width: 60px" />
                <col style="width: 5%; min-width: 60px" />
                <col style="width: 13%; min-width: 140px" />
              </colgroup>
              <thead>
                <tr class="border-b text-left text-gray-500 text-xs uppercase tracking-wide bg-gray-50">
                  <th class="px-3 py-2 font-medium">Member</th>
                  <th class="px-3 py-2 font-medium">Enrolled</th>
                  <th class="px-3 py-2 font-medium">AIP status</th>
                  <th class="px-3 py-2 font-medium">AIP payment</th>
                  <th class="px-3 py-2 font-medium">Source</th>
                  <th class="px-3 py-2 font-medium">Last visit</th>
                  <th class="px-3 py-2 font-medium text-right">Posts</th>
                  <th class="px-3 py-2 font-medium text-right">Replies</th>
                  <th class="px-3 py-2 font-medium">Other courses</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row, i) => (
                  <RosterRowView key={row.userId} row={row} striped={i % 2 === 1} />
                ))}
              </tbody>
            </table>
          </div>
          {filteredRows.length === 0 && (
            <div class="py-8 text-center text-sm text-gray-500">
              No members match the current filter.
            </div>
          )}
        </>
      )}
    </div>
  );
}

function RosterRowView({ row, striped }: { row: RosterRow; striped: boolean }) {
  const aipBadge = AIP_STATUS_CLASS[row.aipStatus];
  const aipPaymentClass = AIP_PAYMENT_CLASS[row.aipPaymentStatus];

  return (
    <tr
      class={`border-b border-gray-100 align-top hover:bg-indigo-50/40 ${striped ? 'bg-gray-50/50' : ''}`}
    >
      <td class="px-3 py-2.5">
        <div class="flex items-center gap-2 min-w-0">
          <span class="font-medium text-gray-900 truncate" title={row.name}>
            {row.name}
          </span>
          {row.kind !== 'member' && (
            <span
              class={`flex-shrink-0 inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold tracking-wide ${KIND_PILL_CLASS[row.kind]}`}
            >
              {KIND_PILL_LABEL[row.kind]}
            </span>
          )}
        </div>
        <a
          class="block text-xs text-gray-500 hover:text-gray-700 truncate"
          href={`mailto:${row.email}`}
          title={row.email}
        >
          {row.email}
        </a>
      </td>

      <td class="px-3 py-2.5 text-gray-700 whitespace-nowrap text-xs">
        {formatShortDate(row.enrolledDate)}
      </td>

      <td class="px-3 py-2.5">
        <span class={`inline-block px-2 py-0.5 rounded text-xs font-medium ${aipBadge}`}>
          {AIP_STATUS_LABEL[row.aipStatus]}
        </span>
        {row.aipStatus === 'cancelled' && row.aipCancelledDate && (
          <div class="text-[11px] text-gray-500 mt-1 whitespace-nowrap">
            {formatShortDate(row.aipCancelledDate)}
          </div>
        )}
      </td>

      <td
        class={`px-3 py-2.5 ${aipPaymentClass}`}
        title={row.aipPriceName ? fullPrice(row.aipPriceName, row.aipPriceUnitAmount) : undefined}
      >
        <div class="truncate">{AIP_PAYMENT_LABEL[row.aipPaymentStatus]}</div>
        {row.aipPriceName && row.aipPaymentStatus !== 'manual' && (
          <div class="text-[11px] text-gray-500 mt-0.5 truncate">
            {compactPrice(row.aipPriceName, row.aipPriceUnitAmount)}
          </div>
        )}
      </td>

      <td class="px-3 py-2.5 text-gray-700">
        <div
          class="truncate"
          title={fullPrice(row.sourcePriceName, row.sourcePriceUnitAmount) || 'Manually added'}
        >
          {compactPrice(row.sourcePriceName, row.sourcePriceUnitAmount) || 'Manually added'}
        </div>
        {row.firstPriorCourseTitle && row.firstPriorEnrolledDate && (
          <div
            class="text-[11px] text-gray-500 mt-0.5 truncate"
            title={`Prior: ${row.firstPriorCourseTitle} (${formatShortDate(row.firstPriorEnrolledDate)})`}
          >
            via {row.firstPriorCourseTitle}
          </div>
        )}
      </td>

      <td class="px-3 py-2.5 text-gray-700 whitespace-nowrap text-xs">
        {formatRelativeDate(row.lastActiveDate)}
      </td>

      <td class="px-3 py-2.5 text-right text-gray-700 tabular-nums">{row.postCount}</td>
      <td class="px-3 py-2.5 text-right text-gray-700 tabular-nums">{row.replyCount}</td>

      <td class="px-3 py-2.5">
        <OtherCoursesCell titles={row.otherCourses} />
      </td>
    </tr>
  );
}

function OtherCoursesCell({ titles }: { titles: string[] }) {
  if (titles.length === 0) {
    return <span class="text-xs text-gray-400">—</span>;
  }
  // Show count chip + first title abbreviated; full list reveals on hover via title.
  const firstTitle = titles[0];
  const more = titles.length - 1;
  const fullList = titles.join(', ');
  return (
    <div class="flex items-center gap-1.5 min-w-0" title={fullList}>
      <span class="inline-flex items-center justify-center px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 text-[11px] font-semibold tabular-nums">
        {titles.length}
      </span>
      <span class="text-xs text-gray-600 truncate">
        {firstTitle}
        {more > 0 && <span class="text-gray-400"> +{more}</span>}
      </span>
    </div>
  );
}
